import React from 'react';
import { Modal, ModalHeader, ModalTitle, ModalContent } from '../../../components/ui/Modal';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import type {
  EmailTemplate,
  EmailTemplateFolder,
  EmailTemplateLibrarySection,
  SharedEmailTemplateFolder,
} from '../useData';
import TemplatesLibraryPanel from './TemplatesLibraryPanel';

export interface OpenTemplateLibraryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onOpenTemplate: (template: EmailTemplate) => void;
  companyTemplates: EmailTemplate[];
  personalTemplates: EmailTemplate[];
  sharedTemplates: EmailTemplate[];
  companyFolders: EmailTemplateFolder[];
  personalFolders: EmailTemplateFolder[];
  sharedFolders: SharedEmailTemplateFolder[];
  companyCanEdit?: boolean;
  companyDocuments?: EmailTemplateLibrarySection['documents'];
  personalDocuments?: EmailTemplateLibrarySection['documents'];
  isLoading?: boolean;
}

const OpenTemplateLibraryModal: React.FC<OpenTemplateLibraryModalProps> = ({
  isOpen,
  onClose,
  onOpenTemplate,
  companyTemplates,
  personalTemplates,
  sharedTemplates,
  companyFolders,
  personalFolders,
  sharedFolders,
  companyCanEdit = false,
  companyDocuments = [],
  personalDocuments = [],
  isLoading,
}) => {
  const { colors } = useTheme();

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalHeader>
        <ModalTitle>Open from library</ModalTitle>
        <button
          type="button"
          onClick={onClose}
          className={cn('p-1.5 rounded-lg', colors.textMuted, colors.interactiveHover)}
          aria-label="Close"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </ModalHeader>
      <ModalContent>
        <div className="-mx-6 -mb-6 -mt-3 h-[min(72vh,680px)] flex flex-col min-h-0">
          <TemplatesLibraryPanel
            pickerMode="template"
            company={{
              folders: companyFolders,
              templates: companyTemplates,
              documents: companyDocuments,
              canEdit: companyCanEdit,
            }}
            personal={{
              folders: personalFolders,
              templates: personalTemplates,
              documents: personalDocuments,
            }}
            shared={{ folders: sharedFolders, templates: sharedTemplates }}
            isLoading={isLoading}
            onPickTemplate={(template) => {
              onOpenTemplate(template);
              onClose();
            }}
            onImported={() => undefined}
            onError={() => undefined}
            onSuccess={() => undefined}
          />
        </div>
      </ModalContent>
    </Modal>
  );
};

export default OpenTemplateLibraryModal;
